import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';

const ProductMultipleSelect = ({
  value = [],
  onChange,
  placeholder,
  label,
  required = false,
  errorText,
  showHelperArea = true,
  disabled = false,
  fill = false,
  translate
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef(null);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    const cleanValue = inputValue.replace(/[^\d]/g, '');
    setInputValue(cleanValue);

    if (cleanValue.length === 8) {
      const exists = value.some(item => item.value === cleanValue);
      if (!exists) {
        const newProduct = {
          value: cleanValue,
          label: cleanValue
        };
        onChange([...value, newProduct]);
        setInputValue('');
      } else {
        setInputValue('');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addProduct();
    }
  };

  const addProduct = () => {
    if (!inputValue.trim()) return;

    const cleanValue = inputValue.trim().replace(/[^\d]/g, '');

    if (cleanValue.length === 8) {
      const exists = value.some(item => item.value === cleanValue);
      if (!exists) {
        const newProduct = {
          value: cleanValue,
          label: cleanValue
        };
        onChange([...value, newProduct]);
        setInputValue('');
      }
    }
  };

  const removeProduct = (indexToRemove) => {
    const newValue = value.filter((_, index) => index !== indexToRemove);
    onChange(newValue);
  };

  const renderSelectedOptions = () => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return null;
    }

    return value.map((product, index) => (
      <span
        key={index}
        className="input-container-select-multiple-option"
        style={{
          backgroundColor: '#d3d2d6',
          color: '#3c3738',
          padding: '.2rem .5rem',
          borderRadius: '20px',
          fontSize: '14px',
          margin: '.1rem',
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        {product.label}
        <span
          className="times"
          onClick={(e) => {
            e.stopPropagation();
            removeProduct(index);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            fontSize: '16px',
            marginLeft: '.3rem',
            cursor: 'pointer'
          }}
        >
          ×
        </span>
      </span>
    ));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("Text")
      .split(/[,\s]+/)
      .filter((item) => /^\d{8}$/.test(item.trim()));

    if (pastedData.length) {
      const uniquePastedData = [...new Set(pastedData)];
      const newProducts = uniquePastedData.map(code => ({
        value: code.trim(),
        label: code.trim()
      })).filter(newProduct =>
        !value.some(existing => existing.value === newProduct.value)
      );
      if (newProducts.length > 0) {
        onChange([...value, ...newProducts]);
        setTimeout(() => {
          const selectedProductsDiv = containerRef.current?.querySelector('.selected-products');
          if (selectedProductsDiv) {
            selectedProductsDiv.scrollTop = selectedProductsDiv.scrollHeight;
          }
        }, 100);
      }
    }
  };

  const isError = errorText && showHelperArea;

  return (
    <div className={`input-container input-container-select ${value && value.length > 0 ? 'filled' : ''}`} style={fill ? { width: '100%', maxWidth: 'unset' } : { width: '100%' }} ref={containerRef}>
      <label>
        {label || translate('price.banner.products.lm')}
        {required !== true && <i> - {translate('optional')}</i>}
      </label>
      <div
        className={`as-input form-control ${isFocused ? 'open' : ''} ${isError ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        style={{
          border: isError ? '1px solid #d32f2f' : (value && value.length > 0) ? '1px solid #add585' : isFocused ? '1px solid #add585' : '1px solid #ddd',
          borderRadius: '8px',
          padding: '0',
          minHeight: '47px',
          maxHeight: '160px',
          backgroundColor: disabled ? '#eeeef0' : 'white',
          cursor: disabled ? 'not-allowed' : 'text',
          margin: '.75rem 0 .25rem 0',
          fontSize: '16px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          position: 'relative'
        }}
      >
        <div className="selected-products" style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          alignItems: 'flex-start',
          minHeight: '47px',
          maxHeight: '160px',
          overflowY: 'auto',
          margin: '0',
          padding: '10px 0 10px 10px'
        }}>
          {renderSelectedOptions()}
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setTimeout(() => {
                setIsFocused(false);
              }, 100);
            }}
            disabled={disabled}
            placeholder={value.length === 0 ? placeholder : ''}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '16px',
              backgroundColor: 'transparent',
              height: '27px',
              padding: '0',
              minWidth: '50px',
              flex: value.length === 0 ? '1' : 'none',
              width: value.length > 0 ? '100px' : 'auto'
            }}
            maxLength={8}
          />
        </div>
      </div>
      {isError && (
        <div className="error-message" style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
          {errorText}
        </div>
      )}
      {showHelperArea && !isError && (
        <div className="helper-text" style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
          {translate('price.banner.product.input')}
        </div>
      )}
    </div>
  );
};

ProductMultipleSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  errorText: PropTypes.string,
  showHelperArea: PropTypes.bool,
  disabled: PropTypes.bool,
  fill: PropTypes.bool,
  translate: PropTypes.func.isRequired
};

export default translate(null, {
  wait: true,
  translateFuncName: 'translate',
})(ProductMultipleSelect);

const scrollStyles = `
  .selected-products::-webkit-scrollbar {
    width: 5px;
  }
  .selected-products::-webkit-scrollbar-thumb {
    border-radius: 20px;
    border: 3px solid #d3d2d6;
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('product-multiple-select-scroll-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'product-multiple-select-scroll-styles';
  styleElement.textContent = scrollStyles;
  document.head.appendChild(styleElement);
}
